import React from 'react';
import Modal from '../../atoms/modal/Modal';
import ModalTitle from '../../atoms/modal/ModalTitle';
import ModalBody from '../../atoms/modal/ModalBody';
import ModalActions from '../../atoms/modal/ModalActions';
import FormWrapper from '../../molecules/form/FormWrapper';
import Button from '../../atoms/button/Button';
import { useForm } from 'react-hook-form';
import { useActions } from '../../hooks/actions';
import { useAppSelector } from '../../hooks/redux';
import FormLabelDropdown from '../../molecules/form/FormLabelDropdown';
import FormFileUpload from '../../molecules/form/FormFileUpload';
import { useImportRoleProfilesMutation } from '../../store/api/uploadFile';
import { ImportFileResponse } from '../../store/api/types/uploadFile';
import { useParams } from 'react-router';

export default function ImportRoleModal() {
  const methods = useForm();

  const { id } = useParams();

  const [fileData, setFileData] = React.useState<File | null>(null);

  const {
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = methods;

  const isOpen = useAppSelector((state) => state.importRoleModal.isOpen);
  const progress = useAppSelector((state) => state.importRoleModal.progress);

  const fileTypeArray = [
    { keyName: 'Role Profiles', keyValue: 'roleProfiles' },
  ];

  const {
    closeImportRoleModal,
    toggleErrorSnackbar,
    toggleSuccessSnackbar,
    setImportRoleUploadProgress,
  } = useActions();

  const [uploadRoles] = useImportRoleProfilesMutation();

  const handleClose = () => {
    setFileData(null);
    closeImportRoleModal();
  };

  const onSubmit = async () => {
    const formData = new FormData();
    if (fileData) {
      formData.append('file', fileData);
      setImportRoleUploadProgress(0);
      uploadRoles({ formData, clientId: id ? id : 'Master' })
        .then((res) => {
          const response = res as ImportFileResponse;
          setImportRoleUploadProgress(0);
          if (response.data) {
            if (response.data.error) {
              toggleErrorSnackbar({ message: response.data.error });
              return;
            }
            if (response.data.status === 'error') {
              toggleErrorSnackbar({ message: response.data.message });
              return;
            }
          }
          if (response.error) {
            toggleErrorSnackbar({ message: response.error.message });
            return;
          }
          toggleSuccessSnackbar({
            message: 'The file has been uploaded successfully',
          });
          handleClose();
        })
        .catch((e) => {
          setImportRoleUploadProgress(0);
          toggleErrorSnackbar({
            message: e.message,
          });
        });
    } else {
      setError('roleFile', { type: 'string', message: 'Please select a file' });
    }
  };

  const handleFileData = (file: File | null) => setFileData(file);

  React.useEffect(() => {
    reset();
  }, [reset, isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={true}
    >
      <ModalTitle onClose={handleClose}>Upload a file.</ModalTitle>
      <ModalBody>
        <FormWrapper
          methods={methods}
          id='add-new-user'
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormLabelDropdown
            name='File type'
            rules={{ required: 'Please select a file type' }}
            label='File type'
            dropDownItem={fileTypeArray}
            placeholder={'Select a file type'}
          />
          <FormFileUpload
            accept='.xlsx,.xls'
            name='roleFile'
            progress={progress}
            rules={{ required: 'Please select a file' }}
            formatErrorMsg='There was an error with the file you provided. Supported formats include Excel files. '
            onChange={handleFileData}
          />
        </FormWrapper>
      </ModalBody>
      {!!fileData && Boolean(progress) === false && (
        <ModalActions>
          <Button
            btnType={'PRIMARY'}
            text={'Upload file'}
            type='submit'
            form='add-new-user'
            disabled={!!Object.values(errors).length || !!progress}
          />
          <Button
            btnType={'SECONDARY'}
            text={'Cancel'}
            onClick={handleClose}
          />
        </ModalActions>
      )}
    </Modal>
  );
}
